import { SelectOption } from '@/components/ui/Select'
import { ToggleOption } from '@/components/ui/ToggleGroup'
import { SchedulingOption } from '@/components/ui/SchedulingToggle'
import { provisionalDiagnoses } from '@/utils/constants/references'

/**
 * Case Constants
 *
 * This file contains all static options and constants used across case-related components.
 * Centralizing these values makes them easier to maintain and ensures consistency.
 */

// ============================================================================
// CALLER INFORMATION OPTIONS
// ============================================================================

// Call direction options
export const callDirectionOptions: ToggleOption[] = [
  {
    value: 'incoming',
    label: 'Incoming Call',
    description:
      'Select this if the case is being initiated by a received call.',
    icon: undefined, // Will be set in the component
  },
  {
    value: 'outgoing',
    label: 'Outgoing Call',
    description: 'Select this if the case begins from a call you initiated.',
    icon: undefined, // Will be set in the component
  },
]

// Gender options (shared between caller and ambulance)
export const genderOptions: SelectOption[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
]

// Prefix options
export const prefixOptions: SelectOption[] = [
  { value: 'mr', label: 'Mr' },
  { value: 'mrs', label: 'Mrs' },
  { value: 'miss', label: 'Miss' },
  { value: 'dr', label: 'Dr' },
  { value: 'prof', label: 'Prof' },
  { value: 'eng', label: 'Eng' },
  { value: 'other', label: 'Other' },
]

// Call type options
export const callTypeOptions: SelectOption[] = [
  { value: 'undetermined', label: 'Undetermined / Ended Without Dispatch' },
  { value: 'general_inquiry', label: 'General Inquiry' },
  { value: 'emergency', label: 'Emergency Case / Evacuation' },
  { value: 'ambulance_request', label: 'Ambulance Request' },
]

// Call category subcategories
export const undeterminedSubcategories: SelectOption[] = [
  { value: 'undetermined/accidental-call', label: 'Accidental Call' },
  { value: 'undetermined/dropped-call', label: 'Dropped Call' },
  { value: 'undetermined/irrelevant-call', label: 'Irrelevant Call' },
]

export const generalInquirySubcategories: SelectOption[] = [
  {
    value: 'general-inquiry/service-information',
    label: 'Service Information',
  },
  { value: 'general-inquiry/pricing-request', label: 'Pricing Request' },
  { value: 'general-inquiry/follow-up', label: 'Follow-up' },
]

export const emergencySubcategories: SelectOption[] = [
  {
    value: 'emergency/dispatch-first-responder',
    label: 'Dispatch ERA First Responder',
  },
]

export const ambulanceRequestSubcategories: SelectOption[] = [
  {
    value: 'ambulance-request/dispatch-era-ambulance',
    label: 'Dispatch ERA Ambulance',
  },
  {
    value: 'ambulance-request/dispatch-3rd-party-ambulance-partner',
    label: 'Dispatch 3rd Party Ambulance Partner',
  },
  {
    value: 'ambulance-request/interstate-transfer',
    label: 'Interstate Transfer',
  },
  {
    value: 'ambulance-request/interstate-hospital-transfer',
    label: 'Interstate Hospital Transfer',
  },
]

// ============================================================================
// AMBULANCE REQUEST INFORMATION OPTIONS
// ============================================================================

// Scheduling options
export const schedulingOptions: SchedulingOption[] = [
  { value: 'now', label: 'Now' },
  { value: 'schedule', label: 'Schedule' },
]

// Consciousness options
export const consciousnessOptions: SelectOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

// Oxygen requirement options
export const oxygenRequiredOptions: SelectOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
]

// Severity level options
export const severityLevelOptions: SelectOption[] = [
  { value: 'low', label: 'Low' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'high', label: 'High' },
]

// Provisional diagnosis options
export const provisionalDiagnosisOptions: SelectOption[] =
  provisionalDiagnoses.provisionalDaignosis.map(diagnosis => {
    return {
      label: diagnosis.name,
      value: diagnosis.name,
    }
  }) as unknown as SelectOption[]

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get subcategories for a specific call type
 * @param callType - The call type to get subcategories for
 * @returns Array of subcategory options
 */
export const getSubcategoriesForCallType = (
  callType: string
): SelectOption[] => {
  switch (callType) {
    case 'undetermined':
      return undeterminedSubcategories
    case 'general_inquiry':
      return generalInquirySubcategories
    case 'emergency':
      return emergencySubcategories
    case 'ambulance_request':
      return ambulanceRequestSubcategories
    default:
      return []
  }
}

/**
 * Get all call category options (flattened)
 * @returns Array of all call category options
 */
export const getAllCallCategoryOptions = (): SelectOption[] => {
  return [
    ...undeterminedSubcategories,
    ...generalInquirySubcategories,
    ...emergencySubcategories,
    ...ambulanceRequestSubcategories,
  ]
}

// ============================================================================
// LEGACY EXPORTS (for backward compatibility)
// ============================================================================

// Re-export with old names for backward compatibility
export const defaultCallTypeOptions = callTypeOptions
export { callDirectionOptions as callDirectionToggleOptions }
